import React, { useEffect, useState } from 'react';
import { PageHeader } from '../../../constants/style/common.styled';
import Heading from '../../../components/typography';
import Search from '../../../components/search/search';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetCommissionList,
  useGetTablePreferences,
  usePostTablePreferences,
  useWealthlaneAwardApi,
} from '../../../hooks';
import Spinners from '../../../components/spinner';
import { commissionListHeader } from '../../../constants/table-headers/commissionListHeader';
import Button from '../../../components/button';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import CommissionModal from './commissionModal';
import InitialValuesForm from '../../../constants/initialValues';
import { ModalStatusType } from '../../../types/common';
import { CommissionService } from '@wealthlane/api-services/wealthlane-award-service';
import AlertPopup from '../../../components/alert-popup';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';
import { CommaFormatted } from '../../../utils/global';
import {
  useDynamicTable,
  DynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import { axiosInstance } from '../../../services/apiInterceptor';

const initialValues = {
  data: InitialValuesForm.commission,
  id: '',
};

const CommissionSettings = () => {
  const { handleRequest } = useWealthlaneAwardApi();
  const [initialData, setInitialData] = useState(initialValues);
  const queryClient = useQueryClient();
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [modalLoading, setModalLoading] = useState(false);
  const [headerList, setHeaderList] = useState<string[]>([]);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'COMMISSION_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });
  const { tableInfo } = useDynamicTable();

  const {
    data: commissionList,
    status,
    isLoading: commissionListLoading,
  } = useGetCommissionList(handleRequest, tableInfo);

  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Commission');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Commission',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const getCommissionById = (id: string): any => {
    void handleRequest(CommissionService.getApiAwardApiAppCommissionCommissionById(id))
      .then((data: any) => {
        if (data != null) {
          setInitialData({
            id,
            data: {
              ...data,
              baseRate: CommaFormatted(data?.baseRate),
              maximumAmount: CommaFormatted(data?.maximumAmount),
              rateBrackets: data?.rateBrackets
                ?.sort((a: any, b: any) => Number(a?.lowerShareLimit) - Number(b?.lowerShareLimit))
                .map((item: any) => ({
                  ...item,
                  rate: CommaFormatted(item?.rate),
                  lowerShareLimit: CommaFormatted(item?.lowerShareLimit),
                })),
            },
          });
          openModal();
        }
      })
      .finally(() => { });
  };
  const onPrimaryClick = (values: Record<string, any>): void => {
    deleteBroadcastMessageById(customValue);
  };

  const deleteBroadcastMessageById = (id: string): void => {
    setModalLoading(true);
    handleRequest(CommissionService.deleteApiAwardApiAppCommissionCommission(id))
      .then((data: any) => {
        if (data != null) {
          toast.success(<ToastComponent label={'Success'} message={'Commission deleted.'} />);
          void queryClient.invalidateQueries('getCommissionList');
          setModalLoading(false);
          closeConfirmModal();
        }
      })
      .catch(() => {
        setModalLoading(false);
        closeConfirmModal();
      })
      .finally(() => {
        setModalLoading(false);
        closeConfirmModal();
      });
  };

  // Table Actions
  const tableActions = [
    {
      id: 'view',
      action: 'View',
      as: 'View',
      onClick: async (id: string, rowData: Record<string, string>) => {
        setModalStatus('View');
        getCommissionById(id);
      },
    },
    {
      id: 'edit',
      action: 'Edit',
      as: 'Edit',
      onClick: async (id: string) => {
        setModalStatus('Edit');
        getCommissionById(id);
      },
    },
    {
      id: 'delete',
      action: <div className='delete-label'>Delete</div>,
      // as: 'Delete',
      onClick: (id: string) => {
        setModalStatus('Delete');
        setCustomValue(id);
        openConfirmModal();
      },
    },
  ];

  const tableHeaderList = commissionListHeader({ actions: schemaData?.columns, tableActions })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const commissionHeader = (): any[] => {
    return commissionListHeader({ actions: schemaData?.columns, tableActions }).filter(
      (item) => item.isSubHeader === false || headerList.includes(item.accessor)
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: commissionHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );

  const data = React.useMemo(
    () => commissionList?.items || [],
    [commissionList, commissionListLoading]
  );

  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  const renderRightToolbarButton = () => {
    return (
      <Button
        as='Create'
        hasId='commission'
        title='Add Commission'
        variant='primary'
        icon={'add_circle_outline'}
        id="commissionAddCommissionButton"
        onClick={() => {
          setModalStatus('Add');
          openModal();
          setInitialData(initialValues);
        }}
      />
    );
  };

  return (
    <div id="commissionPageContainer">
      <PageHeader id="commissionPageHeader">
        <Heading variant='h3' title='Commission' id="commissionPageTitle" />
        <Heading variant='body-s' title={`List of commissions.`} id="commissionPageDescription" />
      </PageHeader>
      <AnimationWrapper id="commissionAnimationWrapper">
        {isModalOpen && (
          <CommissionModal
            id="commissionModal"
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            initialData={initialData}
            handleRequest={handleRequest}
            setModalStatus={setModalStatus}
            loading={false}
            modalStatus={modalStatus}
          />
        )}
      </AnimationWrapper>
      {isConfirmOpen && (
        <AlertPopup
          id="commissionDeleteAlert"
          isShow={isConfirmOpen}
          closeModal={closeConfirmModal}
          modalStatus={modalStatus}
          title={'this Message'}
          handleSubmit={onPrimaryClick}
          // setRemark={setRemark}
          // remark={remark}
          loading={modalLoading}
        />
      )}

      <DynamicTable
        id="commissionDataTable"
        schema={schema}
        data={data}
        loading={status}
        // filterControls={controls}
        totalCounts={commissionList?.totalCount ?? 10}
        components={{ renderRightToolbarButton }}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      />
    </div>
  );
};

export default withDynamicTableContext(CommissionSettings, false, axiosInstance);
